import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Layers, Edit3, Camera } from "lucide-react";
import { useTranslation } from "@/lib/i18n/useTranslation";
import PDFMerge from "@/components/pdf/PDFMerge";
import PDFEditor from "@/components/pdf/PDFEditor";
import DocumentScanner from "@/components/pdf/DocumentScanner";

export interface PendingPdfForSign {
  blob: Blob;
  fileName: string;
}

const PDFTools = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("scan");
  const [pendingPdfForEdit, setPendingPdfForEdit] = useState<PendingPdfForSign | null>(null);

  const handleRequestEdit = useCallback((blob: Blob, fileName: string) => {
    setPendingPdfForEdit({ blob, fileName });
    setActiveTab("edit");
  }, []);

  const handleEditComplete = useCallback(() => {
    setPendingPdfForEdit(null);
  }, []);

  return (
    <div className="min-h-screen bg-background md:ml-16">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 h-14 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="h-9 w-9"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold">{t.pdf.pageTitle}</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-3xl pb-24 md:pb-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Tabs compacts */}
          <Card className="p-1 mb-6">
            <TabsList className="w-full h-11 bg-transparent gap-1">
              <TabsTrigger 
                value="scan" 
                className="flex-1 text-sm gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md transition-all"
              >
                <Camera className="w-4 h-4" />
                <span className="hidden sm:inline">{t.pdf.scan}</span>
                <span className="sm:hidden">Scanner</span>
              </TabsTrigger>
              <TabsTrigger 
                value="merge" 
                className="flex-1 text-sm gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md transition-all"
              >
                <Layers className="w-4 h-4" />
                <span className="hidden sm:inline">{t.pdf.merge}</span>
                <span className="sm:hidden">Fusionner</span>
              </TabsTrigger>
              <TabsTrigger 
                value="edit" 
                className="flex-1 text-sm gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md transition-all"
              >
                <Edit3 className="w-4 h-4" />
                <span className="hidden sm:inline">Éditer</span>
                <span className="sm:hidden">Éditer</span>
              </TabsTrigger>
            </TabsList>
          </Card>

          <TabsContent value="scan" className="mt-0">
            <Card className="p-4 md:p-6">
              <DocumentScanner />
            </Card>
          </TabsContent>

          <TabsContent value="merge" className="mt-0">
            <Card className="p-4 md:p-6">
              <PDFMerge onRequestSign={handleRequestEdit} />
            </Card>
          </TabsContent>

          <TabsContent value="edit" className="mt-0">
            <Card className="p-4 md:p-6">
              <PDFEditor 
                initialPdf={pendingPdfForEdit} 
                onComplete={handleEditComplete}
              />
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default PDFTools;
